/* eslint-disable @typescript-eslint/naming-convention */

import { Alchemy, Network } from 'alchemy-sdk'
import axios, { AxiosInstance } from 'axios'

import { IConfig, IContract, ICoupon, IInventory, TInventoryProvider, TNetworkProviders } from '@types'

import AlgorandNetwork from './AlgorandNetwork'
import EthereumNetwork from './EthereumNetwork'
import {
    NetworkManagerTypes,
    THandleConnectWalletController,
    THandleEstimateGasPrice,
    THandleMint,
    THandleMintCallback,
    THandleWhiteListed,
} from './models'
import PolygonNetwork from './PolygonNetwork'
import { conversionUtils, generalUtils, networkUtils, winterUtils } from './utils'

export class NetworkManager implements NetworkManagerTypes {
    private config: IConfig
    private isLogActive = false
    private walletType = ''
    private networkName: TNetworkProviders
    private alchemyInstance: Alchemy
    private winterInstance: AxiosInstance
    private blockchain?: string
    private projectId?: number

    public provider: any

    private contract?: IContract

    constructor(config: IConfig, networkName: TNetworkProviders = 'ethereum') {
        this.config = config
        this.isLogActive = false
        this.networkName = networkName

        this.alchemyInstance = new Alchemy({ apiKey: process.env.ALCHEMY_API_KEY })
        this.winterInstance = axios.create({
            baseURL: `${process.env.WINTER_BASEPATH}`,
        })
    }

    /* CLASS MANAGEMENT FUNCTIONS */
    private log(message: string, type: 'info' | 'warn' | 'error' = 'info') {
        if (this.isLogActive && type === 'info') {
            console.log('[NETWORK MANAGER]', message)
        }

        if (type === 'warn') console.warn('[NETWORK MANAGER-WARN]', message)
        else if (type === 'error') console.error('[NETWORK MANAGER-ERROR]', message)

        return this
    }

    setBlockchain(blockchain: string) {
        this.blockchain = blockchain
        if (this.blockchain) this.updateAlchemyProvider()

        return this
    }

    setContract(contract: IContract) {
        this.contract = contract

        return this
    }

    setNetworkName(networkName: TNetworkProviders) {
        this.networkName = networkName

        if (networkName) this.updateAlchemyProvider()

        return this
    }

    setProjectId(projectId?: number) {
        if (projectId) this.projectId = projectId

        return this
    }

    setWalletType(walletType: string) {
        this.walletType = walletType

        return this
    }

    getProvider(providerName: 'ethereum'): EthereumNetwork
    getProvider(providerName: 'algorand'): AlgorandNetwork
    getProvider(providerName: 'polygon'): PolygonNetwork
    getProvider(providerName: TNetworkProviders = 'ethereum') {
        if (providerName === 'ethereum') return this.provider as EthereumNetwork
        if (providerName === 'algorand') return this.provider as AlgorandNetwork
        if (providerName === 'polygon') return this.provider as PolygonNetwork

        return this.provider
    }

    private setProvider() {
        if (!this.config) return this.log('Config is not set', 'error')
        if (!this.blockchain) return this.log('Blockchain is not set', 'error')

        let newProvider
        if (this.networkName === 'algorand')
            newProvider = new AlgorandNetwork(this.config.networks.algorand[this.blockchain], this.walletType)
        else if (this.networkName === 'polygon')
            newProvider = new PolygonNetwork(this.config.networks.polygon[this.blockchain], this.walletType)
        else newProvider = new EthereumNetwork(this.config.networks.ethereum[this.blockchain], this.walletType)

        this.provider = newProvider

        return this
    }

    updateProvider() {
        return this.setProvider()
    }

    updateAlchemyProvider() {
        if (!this.blockchain) {
            this.log('Blockchain is not set', 'error')
            return this
        }

        if (!this.networkName) {
            this.log('Network Name is set', 'info')
            return this
        }

        if (this.networkName === 'ethereum' || this.networkName === 'polygon') {
            const configNetwork = this.config.networks[this.networkName][this.blockchain]
            const blockchain = `${this.networkName}_${configNetwork.name}`

            const alchemyChainSwitch: { [key: string]: Network } = {
                ethereum_mainnet: Network.ETH_MAINNET,
                ethereum_ropsteb: Network.ETH_ROPSTEN,
                ethereum_goerli: Network.ETH_GOERLI,
                ethereum_kovan: Network.ETH_KOVAN,
                ethereum_rinkeby: Network.ETH_RINKEBY,
                matic_mainnet: Network.MATIC_MAINNET,
                matic_mumbai: Network.MATIC_MUMBAI,
            }

            this.alchemyInstance = new Alchemy({
                apiKey: configNetwork.api_key,
                network: alchemyChainSwitch[blockchain],
            })
        }

        return this
    }

    /* NETWORK FUNCTIONS */
    async checkWhitelist(collectionId: string, position: number): Promise<THandleWhiteListed> {
        const currentAccountAsArray = await this.provider.getCurrentAccount()

        return await generalUtils.handleCheckWhitelisted(collectionId, currentAccountAsArray[0], position)
    }

    async getBalance(address: string): Promise<string> {
        if (this.networkName === 'ethereum')
            return await this.getProvider(this.networkName).getBalance(this.provider, address)

        return '-1'
    }

    async getConnectedWallet(): Promise<THandleConnectWalletController> {
        if (this.networkName === 'algorand') {
            return await networkUtils[this.networkName].handleConnectWallet(this.provider)
        } else {
            const chainId = this.config.networks[this.networkName][this.blockchain || ''].chain_id
            return await networkUtils[this.networkName].handleConnectWallet(this.provider, chainId)
        }
    }

    async getContractName(contractAddress: string): Promise<string> {
        if (this.contract) {
            return this.networkName === 'algorand'
                ? networkUtils[this.networkName].handleGetContractName(this.provider, [contractAddress])
                : networkUtils[this.networkName].handleGetContractName(
                      this.provider,
                      this.contract.abi,
                      contractAddress,
                  )
        } else {
            this.log('Contract is not set', 'error')
        }

        return ''
    }

    async getGasFeePriceEstimate(
        contractAddress: string,
        currentMintPrice: string,
        position: number,
        coupon: ICoupon,
        category: number,
        selectedOption: number,
        maxPerWallet: number,
    ): Promise<THandleEstimateGasPrice> {
        if (!this.contract) {
            this.log('Contract is not set', 'error')
            return {
                success: false,
                error: 'Contract is not set',
            }
        }

        if (this.networkName === 'algorand') {
            console.log('[ALGORAND WIP]')
        } else {
            return await networkUtils[this.networkName].handleEstimateGasPrice(
                this.provider,
                this.contract.abi,
                contractAddress,
                currentMintPrice,
                position,
                coupon,
                category,
                selectedOption,
                maxPerWallet,
            )
        }

        return {
            success: false,
            error: 'Not implemented',
        }
    }

    async getNfts(
        contractAddress: string[],
        ownerAddress: string,
        provider: TInventoryProvider = 'alchemy',
    ): Promise<IInventory> {
        const MIN_TOKEN_ID_LENGTH = 3

        if (provider === 'useWinter') {
            const winterResponse = await winterUtils.getNFTs(this.winterInstance, ownerAddress, this.projectId)

            if (winterResponse.success) {
                return {
                    collectionName: winterResponse.data.length === 0 ? 'No NFTs' : winterResponse.data[0].metadata.name,
                    items: winterResponse.data.map(item => {
                        return {
                            nft: {
                                animationUrl: conversionUtils.ipfsToHTTP(item.imageURL.videoURL),
                                image: conversionUtils.ipfsToHTTP(item.imageURL.imageURL),
                                name: item.metadata.name,
                                tokenId: (item.metadata.tokenId || 0).toString().padStart(MIN_TOKEN_ID_LENGTH, '0'),
                            },
                            preview: {
                                image: conversionUtils.ipfsToHTTP(item.imageURL.imageURL),
                            },
                        }
                    }),
                    totalItems: winterResponse.data.length,
                }
            }
        } else if (provider === 'alchemy') {
            try {
                const alchemyResponse = await this.alchemyInstance.nft.getNftsForOwner(ownerAddress, {
                    contractAddresses: contractAddress,
                })

                return {
                    collectionName:
                        alchemyResponse.ownedNfts.length === 0 ? 'No NFTs' : alchemyResponse.ownedNfts[0].title,
                    items: alchemyResponse.ownedNfts.map(item => {
                        return {
                            nft: {
                                animationUrl: item.rawMetadata?.animation_url || '',
                                image: conversionUtils.ipfsToHTTP(item.rawMetadata?.image) || '',
                                name: item.rawMetadata?.name || '',
                                tokenId: item.tokenId,
                            },
                            preview: {
                                image: conversionUtils.ipfsToHTTP(item.rawMetadata?.image),
                            },
                        }
                    }),
                    totalItems: alchemyResponse.totalCount,
                }
            } catch {
                return {
                    collectionName: 'No NFTs',
                    items: [],
                    totalItems: 0,
                }
            }
        }

        return {
            collectionName: '',
            items: [],
            totalItems: 0,
        }
    }

    async getRemainedTokenCount(
        contractAddress: string,
        setCurrentContractAddress?: React.Dispatch<React.SetStateAction<number>>,
        setIsSoldOut?: React.Dispatch<React.SetStateAction<boolean>>,
    ): Promise<number> {
        let tokenIdCounter = 0

        if (!this.contract) {
            this.log('Contract is not set', 'error')
            return tokenIdCounter
        }
        if (!this.provider) this.updateProvider()

        if (this.networkName === 'algorand') {
            if (setCurrentContractAddress && setIsSoldOut) {
                tokenIdCounter = await networkUtils[this.networkName].handleGetRemainedTokenCount(
                    this.provider,
                    [contractAddress],
                    setCurrentContractAddress,
                    setIsSoldOut,
                )
            }
        } else {
            tokenIdCounter = await networkUtils[this.networkName].handleGetRemainedTokenCount(
                this.provider,
                this.contract.abi,
                contractAddress,
            )
        }

        return tokenIdCounter
    }

    async mintTransaction(
        contractAddress: string,
        mintPrice: string,
        selectedOption: number,
        position: number,
        coupon: ICoupon,
        category: number,
        maxPerWallet: number,
        callback: THandleMintCallback,
    ): Promise<THandleMint> {
        if (!this.contract) {
            this.log('Contract is not set', 'error')
            return {
                success: false,
                error: 'Contract is not set',
            }
        }
        if (!this.provider) this.updateProvider()

        if (this.networkName === 'algorand') {
            console.log('[ALGORAND WIP]')
            return {
                success: false,
                error: 'Not implemented',
            }
        } else {
            return await networkUtils[this.networkName].handleMint(
                this.provider,
                this.contract.abi,
                contractAddress,
                mintPrice,
                selectedOption,
                position,
                coupon,
                category,
                maxPerWallet,
                callback,
            )
        }
    }
}
